import { ref, watch, computed } from '@vue/composition-api'
import store from '@/shared/store'
import i18n from '@hw-core/libs/i18n'
import getShiftSuggestions from '@hw-core/shift-extractor'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'

export default function useGigList() {
  // Use toast
  const toast = useToast()

  const refInvoiceListTable = ref(null)

  const canDelete = ref(true)

  // Orders table
  const orderTableColumns = [
    { key: 'created', label: i18n.t('gig_page.order_table.header.ordered_at'), sortable: true },
    { key: 'from', label: i18n.t('gig_page.order_table.header.from'), sortable: true },
    { key: 'title', label: i18n.t('gig_page.order_table.header.title'), sortable: true },
    { key: 'data', label: "#Ehdotusta", sortable: true },
    { key: 'actions', label: '' },
  ]

  // Orders table
  const suggestedColumns = [
    { key: 'area', label: "kohde", sortable: true },
    { key: 'start', label: "alkaa", sortable: true },
    { key: 'role', label: "rooli", sortable: true },
    { key: 'requirements', label: "vaatimukset", sortable: true },
    { key: 'action', label: '' },
  ]


  // Table Handlers
  const tableColumns = [
    { key: 'created', label: i18n.t('gig_page.created'), sortable: true },
    { key: 'title', label: i18n.t('gig_page.title'), sortable: true },
    { key: 'startDate', label: i18n.t('gig_page.start_date'), sortable: true },
    { key: 'expDate', label: i18n.t('gig_page.exp_date'), sortable: true },
    { key: 'availableShifts', label: i18n.t('gig_page.available_shifts'), sortable: true },
    { key: 'totalShifts', label: i18n.t('gig_page.total_shifts'), sortable: true },
    { key: 'status', label: i18n.t('gig_page.status'), sortable: true },
    { key: 'actions', label: i18n.t('gig_page.actions'), tdClass: 'text-center' },
  ]
  const perPage = ref(10)
  const totalInvoices = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const statusOptions = ref([
    { label: i18n.t('gig_page.published'), value: 'open' },
    { label: i18n.t('gig_page.draft'), value: 'draft' },
    { label: i18n.t('gig_page.expired'), value: 'expired' },
    { label: i18n.t('gig_page.suggestion'), value: 'suggestion' },
  ])
  const statusFilter = ref(statusOptions.value[0])
  const loadMore = ref(true)
  const isLoading = ref(false)
  const canLoadMore = ref(false)
  const actionChangePage = ref('')
  const userViews = ref([])
  let deleteId = null
  let gigs = []

  const dataMeta = computed(() => {
    const localItemsCount = refInvoiceListTable.value ? refInvoiceListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalInvoices.value,
    }
  })

  const selectedAccount = computed(() => store.state.appAccount.selectedAccount)
  const currentUser = computed(() => store.state.appAuth.authUser)

  const refetchData = () => {
    refInvoiceListTable.value.refresh()
  }

  watch([currentPage, statusFilter, selectedAccount], (newVal, oldVal) => {
    if (newVal[1] !== oldVal[1]) {
      currentPage.value = 1
    }
    refetchData()
  })

  const getSuggestions = (text, callback) => { // TODO where is this used?
    let data = []
    if (text) {
      data = getShiftSuggestions(text)
    }
    callback(data)
  }

  const createFromSuggestion = (id, accept, activeOrderAreaNames) => {
    store.dispatch('appGigs/createFromSuggestion', { id, accept, activeOrderAreaNames, callback: (result, success) => {
      if(!success) {
        toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } else if(accept) {
        toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: i18n.t('success'),
            icon: 'CheckIcon',
            variant: 'success',
            text: 'Vuorot luotu',
          },
        })

        setTimeout(() => {
          refetchData()
        }, 2000)
      } else if(!accept) {
        toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: 'Hylätty',
            icon: 'XIcon',
            variant: 'warning',
            text: 'Ehdotus hylätty.',
          },
        })
      }
    } })
  }


  const fetchOrders = callback => {
    store.dispatch('appGigs/fetchOrders', { callback })
  }

  const fetchGigs = (ctx, callback) => {
    const loggedUser = currentUser.value
    const onlyAreas = loggedUser.role === 'customer' && loggedUser.areas ? loggedUser.areas : null
    isLoading.value = true
    store
      .dispatch('appGigs/fetchGigs', {
        q: searchQuery.value,
        perPage: perPage.value,
        currentPage: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value.value,
        list: gigs,
        accountId: selectedAccount.value.value.id,
        onlyAreas,
        actionChangePage: actionChangePage.value,
      })
      .then(({ data, isMore }) => {
        gigs = data
        canLoadMore.value = isMore
        loadMore.value = false

        callback(gigs)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe tilausten hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        isLoading.value = false
      })
  }
  const fetchUserShifts = ({ assignedTo, firstShiftStart, callback }) => {
    store
      .dispatch('appGigs/fetchUserShifts', {
        assignedTo,
        firstShiftStart,
      })
      .then(shifts => {
        callback(shifts)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe vuorojen hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }
  const onModalOpen = ({ areaId, id, callback }) => {
    if (id) {
      store.dispatch('appGigs/fetchArea', { areaId })
        .then(async response => {
          const assignedArea = response
          let shifts = []
          shifts = await store.dispatch('appGigs/fetchGigShifts', { id })
          callback({ assignedArea, shifts })
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('error'),
              text: 'Virhe alueen hakemisessa',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
    }
  }
  const onCreateGig = ({ gig, shifts, shouldRefresh }) => {
    store.dispatch('appGigs/createGig', { gig, shifts })
      .then(() => {
        if (gig.isPublished) {
          [statusFilter.value] = statusOptions.value // published
        } else {
          [, statusFilter.value] = statusOptions.value // draft
        }
        gigs = []
        if (shouldRefresh) {
          refetchData()
          toast({
            component: ToastificationContent,
            props: {
              title: i18n.t('success'),
              text: 'Vuorot luotu',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const onEditGig = ({
    gig, shifts, processer
  }) => {
    store.dispatch('appGigs/editGig', { gig, shifts, processer })
      .then(() => {
        if (gig.isPublished) {
          [statusFilter.value] = statusOptions.value // published
        } else {
          [, statusFilter.value] = statusOptions.value // draft
        }
        gigs = []
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Vuorot muokattu',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const onGigCreateEdit = ({
    gig, shifts, isEditing, processer, shouldRefresh,
  }) => {
    if (isEditing) {
      onEditGig({
        gig, shifts, processer,
      })
    } else {
      onCreateGig({ gig, shifts, shouldRefresh })
    }
  }

  const onSearchLocation = ({ text, callback }) => {
    if (text) {
      store.dispatch('appGigs/fetchLocation', { text, accountId: selectedAccount.value.value.id }).then(data => {
        callback(data)
      })
    }
  }

  const onDeleteGig = id => {
    deleteId = id
  }

  const confirmDelete = processer => {
    store
      .dispatch('appGigs/deleteGig', { id: deleteId, processer: `${processer.firstName} ${processer.lastName}` })
      .then(() => {
        gigs = []
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Vuorot poistettu',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const checkCanDelete = async id => {
    const shifts = await store.dispatch('appGigs/fetchGigShifts', { id })
    if (shifts.find(shift => shift.status !== 'open' && shift.status !== 'draft')) {
      canDelete.value = false
    } else {
      canDelete.value = true
    }
  }

  const changePage = action => {
    if (action === 'prev' && currentPage.value > 1) {
      currentPage.value -= 1
      actionChangePage.value = 'prev'
    }
    if (action === 'next' && canLoadMore.value) {
      currentPage.value += 1
      actionChangePage.value = 'next'
    }
  }

  const showUserViews = async (item, callback) => {
    if (item?.viewDetail?.viewId) {
      userViews.value = await store.dispatch('appGigs/fetchUserViews', item.viewDetail.viewId)
    }
    callback()
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveInvoiceStatusVariantAndIcon = status => {
    if (status === 'Partial Payment') return { variant: 'warning', icon: 'PieChartIcon' }
    if (status === 'Paid') return { variant: 'success', icon: 'CheckCircleIcon' }
    if (status === 'Downloaded') return { variant: 'info', icon: 'ArrowDownCircleIcon' }
    if (status === 'Draft') return { variant: 'primary', icon: 'SaveIcon' }
    if (status === 'Sent') return { variant: 'secondary', icon: 'SendIcon' }
    if (status === 'Past Due') return { variant: 'danger', icon: 'InfoIcon' }
    return { variant: 'secondary', icon: 'XIcon' }
  }

  const resolveClientAvatarVariant = status => {
    if (status === 'Partial Payment') return 'primary'
    if (status === 'Paid') return 'danger'
    if (status === 'Downloaded') return 'secondary'
    if (status === 'Draft') return 'warning'
    if (status === 'Sent') return 'info'
    if (status === 'Past Due') return 'success'
    return 'primary'
  }

  return {
    fetchGigs,
    tableColumns,
    perPage,
    currentPage,
    totalInvoices,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refInvoiceListTable,
    loadMore,
    canLoadMore,

    statusFilter,
    statusOptions,
    userViews,

    resolveInvoiceStatusVariantAndIcon,
    resolveClientAvatarVariant,

    refetchData,
    onModalOpen,
    onGigCreateEdit,
    onDeleteGig,
    onSearchLocation,
    confirmDelete,
    canDelete,
    checkCanDelete,

    orderTableColumns,
    suggestedColumns,
    fetchOrders,
    getSuggestions,
    createFromSuggestion,
    isLoading,
    changePage,
    showUserViews,
    fetchUserShifts,
  }
}
